import React from 'react';
import PropTypes from 'prop-types';
import {
  CompactTextInput, CompactRangeSlider, CompactToggle, CompactSelect,
  CompactColorPicker, CompactMultiSelect, CompactStringListInput,
} from './CompactPropertyFields';
import { getEditorOptionLabel, getEditorPropertyHelp, getEditorPropertyLabel } from 'features/integration-wizard/embedded/editors/WidgetEditor/editorLocalization';
import CompactJsonInput from './CompactJsonInput';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

export const COMMON_PROPERTY_NAMES = [
  'hidden', 'maxItems', 'productsInRow', 'cardType', 'showPrice', 'showImage',
  'useGrouping', 'maxGroups', 'maxProductsInGroup', 'limitFacets', 'maxColors',
  'collapseDefault', 'collapseKeepOpened', 'options', 'type', 'maxVisiblePages',
  'columns', 'gap', 'padding', 'orientation', 'align', 'title', 'text', 'label',
  'showTitle', 'showCount', 'maxHistory', 'maxSuggestions', 'position',
];
const NAMED_PROPERTIES = new Set(COMMON_PROPERTY_NAMES.concat([
  'className', 'template', 'ratio', 'customCardTemplate', 'cardTemplateMode',
  'facetAnimationSpeed', 'collapseIgnore',
]));

export const humanizePropertyName = key => String(key)
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/^./, character => character.toUpperCase());

const getSchemaDefault = (propertySchema) => {
  const { def, default: defaultValue } = propertySchema || {};
  const value = def !== undefined ? def : defaultValue;
  return typeof value === 'function' ? value() : value;
};

const inferSchemaFromValue = (value) => {
  if (typeof value === 'boolean') return { type: 'boolean' };
  if (typeof value === 'number') return { type: 'number' };
  if (typeof value === 'string') return { type: 'string' };
  if (Array.isArray(value)) return { type: 'array' };
  if (value && typeof value === 'object') return { type: 'object' };
  return {};
};

const resolveValidatorDescriptor = (propertySchema, currentValue) => {
  const schema = propertySchema?.schema || {};
  const validator = propertySchema?.validator || {};
  const descriptor = Object.keys(schema).length > 0 ? schema : validator;
  if (descriptor?.type) return descriptor;
  const inferred = inferSchemaFromValue(getSchemaDefault(propertySchema));
  return inferred.type ? inferred : inferSchemaFromValue(currentValue);
};

// Complex and unknown properties remain editable through the JSON fallback.
export const isRenderableProperty = (propertySchema, currentValue) => Boolean(propertySchema || currentValue !== undefined);

/** Render selected fields while retaining the complete schema for dependencies. */
const DynamicPropertiesForm = ({ properties, values, onChange, fieldKeys, componentType = '', disabled = false }) => {
  const t = useLocalizedMessage();
  const getValue = (key) => (values[key] !== undefined ? values[key] : getSchemaDefault(properties[key]));
  const keys = fieldKeys || Object.keys(properties || {});
  if (!keys.length) {
    return <div className="setting-item-description"><IntlMessages id="ui.searchExperienceV2.editor.properties.dynamicPropertiesForm.noConfigurablePropertiesForThisComponent" /></div>;
  }
  return (
    <div>
      {keys.map((key) => {
        const propertySchema = properties[key] || {};
        if (propertySchema.visibleIf && !Object.entries(propertySchema.visibleIf)
          .every(([dependency, expected]) => getValue(dependency) === expected)) return null;
        if (key === 'customCardTemplate' && getValue('cardTemplateMode') !== 'custom') return null;
        const value = getValue(key);
        const descriptor = resolveValidatorDescriptor(propertySchema, value);
        const { type, min, max, allowedValues } = descriptor;
        const fallbackTitle = NAMED_PROPERTIES.has(key)
          ? t(`searchInterface.inspector.field.${key}`)
          : (propertySchema.label || propertySchema.title || humanizePropertyName(key));
        const title = getEditorPropertyLabel(t, componentType, key, fallbackTitle);
        const description = getEditorPropertyHelp(t, componentType, key) || (propertySchema.description === title ? '' : (propertySchema.description || ''));
        const optionLabel = option => {
          const label = getEditorOptionLabel(t, key, option);
          return label === String(option) ? humanizePropertyName(option) : label;
        };
        const shared = { key, title, description, value, disabled, onChange: next => onChange(key, next) };
        if (type === 'boolean') return <CompactToggle {...shared} />;
        if (type === 'number') return <CompactRangeSlider {...shared} min={min} max={max} step={descriptor.step ?? 1} />;
        if (['enum', 'enum_or_null', 'string'].includes(type) && allowedValues?.length) {
          return <CompactSelect {...shared} items={allowedValues.map(option => ({ value: option, text: optionLabel(option) }))} nullable={type === 'enum_or_null'} />;
        }
        if (type === 'array_enum' && allowedValues?.length) {
          return <CompactMultiSelect {...shared} items={allowedValues.map(option => ({ value: option, text: optionLabel(option) }))} />;
        }
        if (type === 'array_of_strings') return <CompactStringListInput {...shared} />;
        if (type === 'hex_color') return <CompactColorPicker {...shared} />;
        if (type === 'string') return <CompactTextInput {...shared} multiline={key === 'customCardTemplate'} />;
        const expectedType = ['array', 'array_enum', 'array_of_objects'].includes(type) ? 'array'
          : (['object', 'inner_object'].includes(type) ? 'object' : undefined);
        return <CompactJsonInput {...shared} expectedType={expectedType} />;
      })}
    </div>
  );
};

DynamicPropertiesForm.propTypes = {
  properties: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  fieldKeys: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  componentType: PropTypes.string,
};

export default DynamicPropertiesForm;
